import React, { useState, useEffect, useRef, useCallback } from "react";
import Query from "../Popup_Query/Query";
import "./applicationForm.css";
import {
  MapPin, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, User,
  Heart, Globe, Briefcase, DollarSign, Home, History, Shield, Plus,
  X, Calendar, Map, BookOpen, Plane, Building, CreditCard, HeartPulse,
  Scale, Users, GraduationCap, Info, Star, Sparkles, ArrowRight,
  Circle, Upload, Image, FileText, File,Clock,
  XCircle,
} from "lucide-react";

// === CONSTANTS AND UTILITIES ===
const COUNTRY_LIST = [
  "United States", "Canada", "Mexico", "Germany", "France", "Italy", "Spain",
  "United Kingdom", "China", "Japan", "Australia", "New Zealand", "Brazil",
  "Argentina", "South Africa", "Egypt", "Russia", "India", "Pakistan", "Bangladesh",
  "Turkey", "Saudi Arabia", "UAE", "Malaysia", "Singapore", "Thailand", "Indonesia",
  "Vietnam", "South Korea", "Nigeria", "Kenya", "Morocco", "Greece", "Portugal",
  "Sweden", "Norway", "Denmark", "Netherlands", "Belgium", "Switzerland"
];

const DEGREE_OPTIONS = [
  "High School", "Associate Degree", "Bachelor's Degree", "Master's Degree",
  "Doctorate", "Diploma/Certificate", "Uneducated"
];

const DISEASE_OPTIONS = [
  "Tuberculosis", "HIV", "Hepatitis B", "Hepatitis C", "Malaria", "Cancer",
  "Diabetes", "Heart Disease", "Lung Disease", "Kidney Disease", "Liver Disease",
  "Mental Health Issues", "Stroke", "Severe Asthma", "Epilepsy", "Other"
];

const CASE_OPTIONS = [
  "Fraud", "Drug Trafficking", "Human Trafficking", "Money Laundering", "Violent Crime",
  "Theft", "Bribery", "Terrorism", "Smuggling", "Forgery", "Cyber Crime", "Arson",
  "Tax Evasion", "Assault", "Corruption", "Other"
];

const API_BASE_URL = "https://api.truvz.com/api/visa-applications";

// Utility function to process form data
const processFormData = (data) => {
  const processed = { ...data };
  
  // Convert empty date strings to null
  const dateFields = ['date_of_birth', 'spouse_dob', 'job_start_date', 'job_end_date'];
  dateFields.forEach(field => {
    if (processed[field] === '') processed[field] = null;
  });
  
  // Process children details
  if (processed.children_details) {
    processed.children_details = processed.children_details.map(child => ({
      ...child,
      dob: child.dob === '' ? null : child.dob
    }));
  }
  
  return processed;
};

// === REUSABLE COMPONENTS ===
function ProgressBar({ currentStep, totalSteps }) {
  const percentage = (currentStep / totalSteps) * 100;

  return (
    <div className="progress-container">
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${percentage}%` }}></div>
      </div>
      <div className="progress-info">
        <div className="step-counter">
          Step {currentStep} of {totalSteps}
        </div>
        <div className="percentage">
          {Math.round(percentage)}% Complete
        </div>
      </div>
    </div>
  );
}

function Stepper({ currentStep, steps }) {
  return (
    <div className="stepper-container">
      <div className="stepper">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = currentStep > stepNumber;
          const isActive = currentStep === stepNumber;
          const isFuture = currentStep < stepNumber;

          return (
            <div
              key={index}
              className={`stepper-item ${isCompleted ? "completed" : ""} ${isActive ? "active" : ""} ${isFuture ? "future" : ""}`}
            >
              <div className="stepper-circle">
                {isCompleted ? <CheckCircle size={18} /> : stepNumber}
              </div>
              <div className="stepper-label">{step}</div>
              {index < steps.length - 1 && (
                <div className={`stepper-line ${isCompleted ? "completed" : ""}`}></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FormSection({ title, icon: Icon, children, className = "", description, isComplete = false }) {
  return (
    <div className={`form-section ${className} ${isComplete ? 'completed' : ''}`}>
      <div className="section-header">
        <div className="section-title-container">
          {Icon && (
            <div className="section-icon-wrapper">
              <Icon size={28} className="section-icon" />
            </div>
          )}
          <div className="title-and-badge">
            <h3 className="section-title">{title}</h3>
            {isComplete && (
              <span className="complete-badge">
                <CheckCircle size={14} /> Completed
              </span>
            )}
          </div>
        </div>
        {description && <p className="section-description">{description}</p>}
      </div>
      <div className="section-content">{children}</div>
    </div>
  );
}

function FormInput({
  label,
  type = "text",
  name,
  value,
  onChange,
  placeholder,
  className = "",
  required = false,
  icon: Icon,
  readOnly = false,
  error = "",
  ...props
}) {
  return (
    <div className={`form-group ${Icon ? "has-icon" : ""} ${error ? "has-error" : ""}`}>
      <label className="form-label">
        {label}
        {required && <span className="required">*</span>}
      </label>
      <div className="input-container">
        {Icon && <Icon size={18} className="input-icon" />}
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          className={`form-input ${className} ${readOnly ? 'read-only' : ''}`}
          value={value || ""}
          onChange={onChange}
          required={required}
          readOnly={readOnly}
          {...props}
        />
      </div>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}

function FormSelect({ label, value, onChange, children, className = "", required = false, icon: Icon, error = "", disabled = false }) {
  return (
    <div className={`form-group ${Icon ? "has-icon" : ""} ${error ? "has-error" : ""}`}>
      <label className="form-label">
        {label}
        {required && <span className="required">*</span>}
      </label>
      <div className="input-container">
        {Icon && <Icon size={18} className="input-icon" />}
        <select
          className={`form-select ${className}`}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
        >
          {children}
        </select>
        <div className="select-arrow">
          <ChevronRight size={16} />
        </div>
      </div>
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}

function FormTextarea({ label, value, onChange, placeholder, rows = 3, className = "", required = false, error = "", readOnly = false }) {
  return (
    <div className={`form-group ${error ? "has-error" : ""}`}>
      <label className="form-label">
        {label}
        {required && <span className="required">*</span>}
      </label>
      <textarea
        placeholder={placeholder}
        className={`form-textarea ${className} ${readOnly ? 'read-only' : ''}`}
        value={value || ""}
        onChange={onChange}
        rows={rows}
        required={required}
        readOnly={readOnly}
      />
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}

function YesNoButtons({ value, onChange, label, required = false, disabled = false }) {
  return (
    <div className={`form-group yesno-group ${disabled ? 'disabled' : ''}`}>
      <label className="form-label">
        {label}
        {required && <span className="required">*</span>}
      </label>
      <div className="yesno-buttons">
        <button
          type="button"
          className={`yesno-button ${value === "yes" ? "selected yes" : ""}`}
          onClick={() => !disabled && onChange("yes")}
          disabled={disabled}
        >
          <CheckCircle size={16} /> Yes
        </button>
        <button
          type="button"
          className={`yesno-button ${value === "no" ? "selected no" : ""}`}
          onClick={() => !disabled && onChange("no")}
          disabled={disabled}
        >
          <X size={16} /> No
        </button>
      </div>
    </div>
  );
}

function OptionButtons({ options, value, onChange, disabled = false }) {
  return (
    <div className="option-buttons-group">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`option-button ${value === option ? "selected" : ""} ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && onChange(option)}
          disabled={disabled}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function NavigationButtons({ onBack, onSave, onContinue, isSaving, showSave = true, continueLabel = "Continue" }) {
  return (
    <div className="form-navigation">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="nav-button back-button"
        >
          <ChevronLeft size={18} /> Back
        </button>
      )}
      
      {showSave && (
        <button
          type="button"
          onClick={onSave}
          className="nav-button save-button"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <div className="spinner"></div>
              Saving...
            </>
          ) : (
            <>
              <CheckCircle size={18} /> Save Progress
            </>
          )}
        </button>
      )}
      
      <button
        type="button"
        onClick={onContinue}
        className="nav-button next-button primary-gradient"
        disabled={isSaving || continueLabel.toLowerCase() === "checking..."}
      >
        {continueLabel} <ArrowRight size={18} />
      </button>
    </div>
  );
}

function ChildDetails({ index, child, onChange, errors }) {
  return (
    <div className="info-card child-card">
      <div className="card-header">
        <div className="card-title-wrapper">
          <span className="card-title">Child {index + 1} Details</span>
          <div className="child-icon">
            <Users size={20} />
          </div>
        </div>
      </div>

      <div className="form-grid">
        <FormInput
          label="Full Name"
          value={child.name}
          onChange={(e) => onChange(index, "name", e.target.value)}
          placeholder="Child's Full Name"
          icon={User}
          error={errors?.name}
        />

        <div className="form-group">
          <label className="form-label">Gender</label>
          <OptionButtons
            options={["Male", "Female", "Other"]}
            value={child.gender}
            onChange={(value) => onChange(index, "gender", value)}
          />
        </div>

        <FormInput
          label="Date of Birth"
          type="date"
          value={child.dob}
          onChange={(e) => onChange(index, "dob", e.target.value)}
          icon={Calendar}
          error={errors?.dob}
        />

        <FormInput
          label="Nationality"
          value={child.nationality}
          onChange={(e) => onChange(index, "nationality", e.target.value)}
          placeholder="Child's Nationality"
          icon={Globe}
          error={errors?.nationality}
        />
      </div>
    </div>
  );
}

function FileUpload({ files, setFiles, maxFiles = 10, maxSizeMB = 5 }) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const handleFileChange = (newFiles) => {
    const validFiles = Array.from(newFiles).filter(file => {
      if (file.size > maxSizeBytes) {
        alert(`File ${file.name} is too large. Maximum size is ${maxSizeMB}MB.`);
        return false;
      }
      return true;
    });

    if (files.length + validFiles.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files.`);
      return;
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange(droppedFiles);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    if (file?.type.startsWith('image/')) return <Image size={20} />;
    if (file?.mime_type.startsWith('image/')) return <Image size={20} />;
    if (file.type === 'application/pdf') return <FileText size={20} />;
    return <File size={20} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="file-upload-section space-y-4">
      <div className="flex items-center gap-2 text-gray-800">
        <h4 className="font-semibold text-lg">Upload Supporting Documents</h4>
      </div>

      <div
        className={`relative border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all duration-200 ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-500'
          }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center justify-center text-center gap-2">
          <Upload size={28} className="text-gray-500" />
          <p className="text-gray-700 font-medium">
            {isDragging ? 'Drop files here' : 'Click or drag files to upload'}
          </p>
          <p className="text-sm text-gray-400">
            Maximum {maxFiles} files, {maxSizeMB}MB each
          </p>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => handleFileChange(e.target.files)}
          multiple
          style={{ display: 'none' }}
        />
      </div>

      {files.length > 0 && (
        <div className="uploaded-files">
          <div className="flex justify-between items-center mb-2 text-gray-600 font-medium">
            <span>{files.length} file{files.length !== 1 ? 's' : ''} uploaded</span>
            {files.length >= maxFiles && (
              <span className="text-red-500 text-sm font-semibold">Maximum files reached</span>
            )}
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {files.map((file, index) => (
  <div key={index} className="relative border rounded-lg p-2 bg-white shadow-sm hover:shadow-md transition-shadow">
    <div className="relative w-full h-20 flex items-center justify-center overflow-hidden rounded-md bg-gray-100" style={{"alignItems":"top"}}>
      {file && (file.type?.startsWith('image/') || file.mime_type?.startsWith('image/')) ? (
        // Check if it has a 'name' property (which server files don't have) or use other distinguishing features
        // More reliable check: real File objects have a 'name' property, server objects have 'original_name'
        file.name ? (
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            className="object-cover w-full h-full"
          />
        ) : (
          // For server files, show a generic image icon
          <div className="text-gray-500 text-xl">
            <Image size={20} />
          </div>
        )
      ) : (
        <div className="text-gray-500 text-xl">
          {getFileIcon(file)}
        </div>
      )}
      <button
        className="absolute top-1 right-1 text-white hover:bg-red-600 transition"
        style={{"background": "red", "borderRadius":"100%", "padding":"6px 12px", "position":"relative","top":"-46px","right":"22px"}}
        onClick={(e) => { e.stopPropagation(); removeFile(index); }}
        title="Remove file"
      >
        <X size={17} />
      </button>
    </div>
    <div className="mt-2 text-xs text-gray-700 truncate" title={file.name || file.original_name}>
      {(file.name || file.original_name).length > 20 ? `${(file.name || file.original_name).substring(0, 17)}...` : (file.name || file.original_name)}
    </div>
    <div className="text-xs text-gray-400">{formatFileSize(file.size)}</div>
  </div>
))}
          </div>
        </div>
      )}
    </div>
  );
}

function SuccessMessage({ show, message = "Application submitted successfully!" }) {
  if (!show) return null;

  return (
    <div className="success-overlay">
      <div className="success-message">
        <div className="success-icon">
          <CheckCircle size={48} />
        </div>
        <h3>Thank You!</h3>
        <p>{message}</p>
        <div className="success-progress">
          <div className="success-progress-bar"></div>
        </div>
      </div>
    </div>
  );
}

// === FORM STEP COMPONENTS ===
// function PersonalInformation({ data, setData, onComplete, onBack, onSaveProgress, isExistingApplication }) {
function PersonalInformation({ data, setData, onComplete, onBack, onSaveProgress, isExistingApplication, isCheckingEmail }) {

  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For date fields, convert empty strings to null
    const processedValue = (name === 'date_of_birth' || name === 'spouse_dob') && value === '' 
      ? null 
      : value;
    
    setData((prev) => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleChange2 = (field, value) => {
    setData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleChildrenSelect = (number_of_children) => {
    const children_details = Array.from({ length: number_of_children }, () => ({
      name: "",
      dob: "",
      nationality: "",
      gender: "",
    }));
    setData((prev) => ({ ...prev, number_of_children, children_details }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Only email is required
    if (!data.email) {
      newErrors.email = 'Email is required';
    }

    // Email validation
    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(data)));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 1); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  // For child details
  const handleChildChange = (index, field, value) => {
    // For date fields in children, convert empty strings to null
    const processedValue = (field === 'dob') && value === '' 
      ? null 
      : value;
    
    const updatedChildrenDetails = data.children_details.map((c, i) =>
      i === index ? { ...c, [field]: processedValue } : c
    );
    setData((prev) => ({ ...prev, children_details: updatedChildrenDetails }));
  };

  const fields = [
    { label: "Full Name", name: "full_name", type: "text", icon: User, required: false },
    { label: "Date of Birth", name: "date_of_birth", type: "date", icon: Calendar, required: false },
    { label: "Nationality", name: "nationality", type: "text", icon: Globe, required: false },
    { label: "Email", name: "email", type: "email", icon: User, required: true },
    { label: "Contact Number", name: "contact_number", type: "tel", icon: User, required: false },
  ];

  return (
    <FormSection
      title="Personal Information"
      icon={User}
      description="Please provide your basic personal details. Only email is required to continue."
      isComplete={isComplete}
    >
      <div className="form-grid">
        {fields.map((field) => (
          <FormInput
            key={field.name}
            label={field.label}
            type={field.type}
            name={field.name}
            value={data[field.name]}
            onChange={handleChange}
            placeholder={field.label}
            required={field.required}
            icon={field.icon}
            error={errors[field.name]}
          />
        ))}

        <FormTextarea
          label="Address"
          value={data.address || ""}
          onChange={(e) => handleChange2("address", e.target.value)}
          placeholder="Your complete address"
          required={false}
          error={errors.address}
        />

        <FormSelect
          label="Marital Status"
          value={data.marital_status}
          onChange={(e) => handleChange2("marital_status", e.target.value)}
          required={false}
          icon={Heart}
          error={errors.marital_status}
        >
          <option value="">Select your status</option>
          <option value="Single">Single</option>
          <option value="Married">Married</option>
          <option value="Separated">Separated</option>
        </FormSelect>

        {data.marital_status === "Married" && (
          <>
            <FormInput
              label="Spouse's Full Name"
              value={data.spouse_name}
              onChange={(e) => handleChange2("spouse_name", e.target.value)}
              placeholder="Spouse's Full Name"
              required={false}
              icon={User}
              error={errors.spouse_name}
            />
            <FormInput
              label="Spouse's Date of Birth"
              type="date"
              value={data.spouse_dob}
              onChange={(e) => handleChange2("spouse_dob", e.target.value)}
              required={false}
              icon={Calendar}
              error={errors.spouse_dob}
            />
            <FormInput
              label="Spouse's Nationality"
              value={data.spouse_nationality}
              onChange={(e) => handleChange2("spouse_nationality", e.target.value)}
              placeholder="Spouse's Nationality"
              required={false}
              icon={Globe}
              error={errors.spouse_nationality}
            />
          </>
        )}

        {(data.marital_status === "Married" || data.marital_status === "Separated") && (
          <>
            <YesNoButtons
              label="Do you have children?"
              value={data.has_children}
              onChange={(value) => {
                const has_children = value === "yes";
                const children_details = has_children ? [{ name: "", dob: "", nationality: "", gender: "" }] : [];
                setData((prev) => ({
                  ...prev,
                  has_children: value,
                  number_of_children: has_children ? 1 : 0,
                  children_details,
                }));

                // Clear children errors
                if (errors.number_of_children) {
                  setErrors(prev => ({ ...prev, number_of_children: '' }));
                }
              }}
              required={false}
            />

            {data.has_children === "yes" && (
              <div className="children-section">
                <div className="section-subheader">
                  <Users size={20} />
                  <h4>Children Information</h4>
                </div>

                <FormSelect
                  label="How many children?"
                  value={data.number_of_children}
                  onChange={(e) => handleChildrenSelect(parseInt(e.target.value, 10))}
                  required={false}
                  error={errors.number_of_children}
                >
                  <option value="">Select number of children</option>
                  {[...Array(11).keys()].slice(1).map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </FormSelect>

                <div className="children-details">
                  {data.children_details.map((child, index) => (
                    <ChildDetails
                      key={index}
                      index={index}
                      child={child}
                      onChange={handleChildChange}
                      errors={errors[`child_${index}`] || {}}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <NavigationButtons
        onBack={onBack}
        onSave={() => saveProgress(false)}
        onContinue={handleContinue}
        isSaving={isSaving}
        continueLabel={isCheckingEmail ? "checking..." : "Continue"}
      />
    </FormSection>
  );
}

function Education({ data, setData, globalMaritalStatus, onComplete, onBack, onSaveProgress }) {
  const [user_education_forms, setUserEducationForms] = useState(data.user_education_forms || []);
  const [spouse_education_forms, setSpouseEducationForms] = useState(data.spouse_education_forms || []);
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    // No validation required - all fields are optional
    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify({
      ...processFormData(data),
      user_education_forms,
      spouse_education_forms
    }));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 2); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleAddEducation = (degree, isSpouse = false) => {
    if (degree && degree !== "Uneducated") {
      const newForm = {
        degree,
        fieldOfStudy: "",
        institution: "",
        completionYear: "",
      };
      if (isSpouse) {
        const updatedForms = [
          ...spouse_education_forms.filter((form) => form.degree !== degree),
          newForm,
        ];
        setSpouseEducationForms(updatedForms);
        setData({ ...data, spouse_education_forms: updatedForms });
      } else {
        const updatedForms = [
          ...user_education_forms.filter((form) => form.degree !== degree),
          newForm,
        ];
        setUserEducationForms(updatedForms);
        setData({ ...data, user_education_forms: updatedForms });
      }
    } else if (degree === "Uneducated") {
      if (isSpouse) {
        setSpouseEducationForms([]);
        setData({ ...data, spouse_education_forms: [] });
      } else {
        setUserEducationForms([]);
        setData({ ...data, user_education_forms: [] });
      }
    }
  };

  const handleEducationChange = (index, field, value, isSpouse = false) => {
    const updateForms = (prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms[index] = { ...updatedForms[index], [field]: value };
      return updatedForms;
    };
    if (isSpouse) {
      const updatedForms = updateForms(spouse_education_forms);
      setSpouseEducationForms(updatedForms);
      setData({ ...data, spouse_education_forms: updatedForms });
    } else {
      const updatedForms = updateForms(user_education_forms);
      setUserEducationForms(updatedForms);
      setData({ ...data, user_education_forms: updatedForms });
    }
  };

  const renderEducationForms = (forms, isSpouse = false) =>
    forms.map((form, index) => (
      <div key={index} className="info-card education-card">
        <div className="card-header">
          <div className="card-title-wrapper">
            <span className="card-title">{form.degree}</span>
            <div className="education-icon">
              <BookOpen size={20} />
            </div>
          </div>
          <button
            className="remove-btn"
            onClick={() => {
              if (isSpouse) {
                const updatedForms = spouse_education_forms.filter((_, i) => i !== index);
                setSpouseEducationForms(updatedForms);
                setData({ ...data, spouse_education_forms: updatedForms });
              } else {
                const updatedForms = user_education_forms.filter((_, i) => i !== index);
                setUserEducationForms(updatedForms);
                setData({ ...data, user_education_forms: updatedForms });
              }
            }}
          >
            <X size={16} />
          </button>
        </div>
        <div className="form-grid">
          <FormInput
            label="Field of Study"
            placeholder="Your major or field of study"
            value={form.fieldOfStudy}
            onChange={(e) =>
              handleEducationChange(index, "fieldOfStudy", e.target.value, isSpouse)
            }
          />
          <FormInput
            label="Institution Attended"
            placeholder="Name of school or university"
            value={form.institution}
            onChange={(e) =>
              handleEducationChange(index, "institution", e.target.value, isSpouse)
            }
            icon={Building}
          />
          <FormInput
            label="Completion Year"
            type="date"
            value={form.completionYear}
            onChange={(e) =>
              handleEducationChange(index, "completionYear", e.target.value, isSpouse)
            }
            icon={Calendar}
          />
        </div>
      </div>
    ));

  return (
    <FormSection
      title="Education History"
      icon={GraduationCap}
      description="Please provide details of your educational background. Add all relevant qualifications."
      isComplete={isComplete}
    >
      <div className="education-container">
        <div className="form-group">
          <FormSelect
            label="Your Highest Degree"
            onChange={(e) => handleAddEducation(e.target.value)}
            icon={BookOpen}
            error={errors.education}
          >
            <option value="">Select Your Highest Degree</option>
            {DEGREE_OPTIONS.map((degree) => (
              <option key={degree} value={degree}>
                {degree}
              </option>
            ))}
          </FormSelect>

          {renderEducationForms(user_education_forms)}

          {["Married"].includes(globalMaritalStatus) && (
            <>
              <div className="section-divider">
                <div className="divider-line"></div>
                <div className="divider-text">Spouse's Education</div>
                <div className="divider-line"></div>
              </div>

              <FormSelect
                label="Spouse's Highest Degree"
                onChange={(e) => handleAddEducation(e.target.value, true)}
                icon={BookOpen}
              >
                <option value="">Select Spouse's Highest Degree</option>
                {DEGREE_OPTIONS.map((degree) => (
                  <option key={degree} value={degree}>
                    {degree}
                  </option>
                ))}
              </FormSelect>

              {renderEducationForms(spouse_education_forms, true)}
            </>
          )}
        </div>

        {errors.education && (
          <div className="error-banner">
            <AlertCircle size={18} /> {errors.education}
          </div>
        )}

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
      </div>
    </FormSection>
  );
}

function TravelInformation({ data, setData, onComplete, onBack, onSaveProgress }) {
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    // No validation required - travel history is optional
    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(data)));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 3); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleCountrySelect = (e) => {
    const selectedCountry = e.target.value;
    if (selectedCountry && !data.travel_info.countries_traveled.includes(selectedCountry)) {
      setData((prev) => ({
        ...prev,
        travel_info: {
          ...prev.travel_info,
          countries_traveled: [...prev.travel_info.countries_traveled, selectedCountry],
        },
        country_details: {
          ...prev.country_details,
          [selectedCountry]: {
            date_visited: "",
            date_return: "",
            purpose: "",
            duration: "",
          },
        },
      }));
    }
  };

  const removeCountry = (country) => {
    setData((prev) => {
      const updatedCountries = prev.travel_info.countries_traveled.filter((c) => c !== country);
      const newDetails = { ...prev.country_details };
      delete newDetails[country];
      return {
        ...prev,
        travel_info: { ...prev.travel_info, countries_traveled: updatedCountries },
        country_details: newDetails,
      };
    });
  };

  const handleDetailChange = (country, field, value) => {
    const updatedDetails = { ...data.country_details[country], [field]: value };
    if ((field === "date_visited" || field === "date_return") && updatedDetails.date_visited && updatedDetails.date_return) {
      const start = new Date(updatedDetails.date_visited);
      const end = new Date(updatedDetails.date_return);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        updatedDetails.duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)).toString();
      }
    }
    setData((prev) => ({
      ...prev,
      country_details: {
        ...prev.country_details,
        [country]: updatedDetails,
      },
    }));
  };

  return (
    <FormSection
      title="Travel History"
      icon={Plane}
      description="List all countries you've visited in the past 10 years. This information is important for your visa application."
      isComplete={isComplete}
    >
      <div className="travel-container">
        <FormSelect
          label="Add a country you've visited"
          onChange={handleCountrySelect}
          value=""
        >
          <option value="" disabled>Select Country</option>
          {COUNTRY_LIST.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </FormSelect>

        {data.travel_info.countries_traveled.length > 0 ? (
          <div className="selected-countries-container">
            <div className="countries-header">
              <h4 className="sub-section-title">
                <Sparkles size={18} /> Countries Visited
              </h4>
              <div className="countries-count">
                {data.travel_info.countries_traveled.length} {data.travel_info.countries_traveled.length === 1 ? 'country' : 'countries'}
              </div>
            </div>

            {data.travel_info.countries_traveled.map((country) => (
              <div key={country} className="country-card">
                <div className="country-header">
                  <div className="country-flag">
                    <Globe size={20} />
                  </div>
                  <span className="country-name">{country}</span>
                  <button className="remove-country-btn" onClick={() => removeCountry(country)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="country-details-grid">
                  <FormInput
                    label="Date Visited"
                    type="date"
                    value={data.country_details[country]?.date_visited || ""}
                    onChange={(e) => handleDetailChange(country, "date_visited", e.target.value)}
                    icon={Calendar}
                  />
                  <FormInput
                    label="Date Return"
                    type="date"
                    value={data.country_details[country]?.date_return || ""}
                    onChange={(e) => handleDetailChange(country, "date_return", e.target.value)}
                    icon={Calendar}
                  />
                  <FormInput
                    label="Duration of Stay (days)"
                    type="text"
                    value={data.country_details[country]?.duration || ""}
                    readOnly
                  />
                  <FormInput
                    label="Purpose of Visit"
                    type="text"
                    placeholder="e.g., Tourism, Business"
                    value={data.country_details[country]?.purpose || ""}
                    onChange={(e) => handleDetailChange(country, "purpose", e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Plane size={48} className="empty-icon" />
            <h4>No travel history added</h4>
            <p>Select a country from the dropdown above to add your travel history.</p>
          </div>
        )}

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
      </div>
    </FormSection>
  );
}

function LivingHistory({ data, setData, onComplete, onBack, onSaveProgress }) {
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    // No validation required - all fields are optional
    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(data)));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 4); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleAddCountry = (e) => {
    const selectedCountry = e.target.value;
    if (selectedCountry && !data.living_history.some((item) => item.country === selectedCountry)) {
      setData((prev) => ({
        ...prev,
        living_history: [
          ...prev.living_history,
          { country: selectedCountry, start_date: "", end_date: "", is_current: false },
        ],
      }));
    }
  };

  const removeCountry = (index) => {
    setData((prev) => ({
      ...prev,
      living_history: prev.living_history.filter((_, i) => i !== index),
    }));
  };

  const handleDetailChange = (index, field, value) => {
    setData((prev) => {
      const updatedHistory = prev.living_history.map((item, i) => {
        if (i === index) {
          if (field === "is_current" && value) {
            return { ...item, is_current: true, end_date: "" };
          }
          return { ...item, [field]: value };
        }
        return field === "is_current" && value ? { ...item, is_current: false } : item;
      });
      return { ...prev, living_history: updatedHistory };
    });
  };

  return (
    <FormSection
      title="Living History (Past 10 Years)"
      icon={Home}
      description="Please provide details of all countries you've lived in during the past 10 years."
      isComplete={isComplete}
    >
      <div className="living-container">
        <FormSelect
          label="Add a country you've lived in"
          onChange={handleAddCountry}
          value=""
        >
          <option value="" disabled>Select Country</option>
          {COUNTRY_LIST.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </FormSelect>

        {errors.living_history && (
          <div className="error-banner">
            <AlertCircle size={18} /> {errors.living_history}
          </div>
        )}

        {data.living_history.length > 0 ? (
          <div className="selected-items-container">
            <div className="items-header">
              <h4 className="sub-section-title">
                <Map size={18} /> Countries Lived In
              </h4>
              <div className="items-count">
                {data.living_history.length} {data.living_history.length === 1 ? 'country' : 'countries'}
              </div>
            </div>

            {data.living_history.map((item, index) => (
              <div key={index} className="info-card living-card">
                <div className="card-header">
                  <div className="card-title-wrapper">
                    <span className="card-title">{item.country}</span>
                    <div className="living-icon">
                      <Home size={20} />
                    </div>
                  </div>
                  <button className="remove-btn" onClick={() => removeCountry(index)}>
                    <X size={16} />
                  </button>
                </div>
                <div className="card-content">
                  <div className="form-grid">
                    <FormInput
                      label="Start Date"
                      type="date"
                      value={item.start_date || ""}
                      onChange={(e) => handleDetailChange(index, "start_date", e.target.value)}
                      icon={Calendar}
                    />
                    {!item.is_current && (
                      <FormInput
                        label="End Date"
                        type="date"
                        value={item.end_date || ""}
                        onChange={(e) => handleDetailChange(index, "end_date", e.target.value)}
                        icon={Calendar}
                      />
                    )}
                  </div>
                  <div className="checkbox-field">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={item.is_current}
                        onChange={(e) => handleDetailChange(index, "is_current", e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      Currently living here
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Map size={48} className="empty-icon" />
            <h4>No countries added</h4>
            <p>Select a country from the dropdown above to add your living history.</p>
          </div>
        )}

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
      </div>
    </FormSection>
  );
}

function Employment({ data, setData, onComplete, onBack, onSaveProgress }) {
  const [employment_status, setEmploymentStatus] = useState(data.employment_status || "");
  const [employment_records, setEmploymentRecords] = useState(data.employment_records || []);
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    // No validation required - all fields are optional
    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify({
      ...processFormData(data),
      employment_status,
      employment_records
    }));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 5); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setEmploymentStatus(status);
    setData((prev) => ({ ...prev, employment_status: status }));
    if (status !== "Employed") {
      setEmploymentRecords([]);
      setData((prev) => ({ ...prev, employment_records: [] }));
    }
  };

  const addRecord = () => {
    const newRecord = {
      employer_details: "",
      job_title: "",
      salary: "",
      job_start_date: "",
      job_end_date: "",
      country: "",
      is_current_job: false,
    };
    const updated = [...employment_records, newRecord];
    setEmploymentRecords(updated);
    setData({ ...data, employment_records: updated });
  };

  const updateRecord = (index, field, value) => {
    // For date fields in employment, convert empty strings to null
    const processedValue = (field === 'job_start_date' || field === 'job_end_date') && value === '' 
      ? null 
      : value;
    
    const updated = [...employment_records];
    updated[index][field] = processedValue;
    if (field === "is_current_job" && value) {
      updated.forEach((r, i) => {
        if (i !== index) r.is_current_job = false;
      });
    }
    setEmploymentRecords(updated);
    setData({ ...data, employment_records: updated });
  };

  return (
    <FormSection
      title="Employment History (Past 5 Years)"
      icon={Briefcase}
      description="Please provide details of your employment history for the past 5 years."
      isComplete={isComplete}
    >
      <div className="employment-container">
        <FormSelect
          label="Current Employment Status"
          value={employment_status}
          onChange={handleStatusChange}
          icon={Briefcase}
          error={errors.employment_status}
        >
          <option value="" disabled>Select Employment Status</option>
          <option value="Employed">Employed</option>
          <option value="Unemployed">Unemployed</option>
          <option value="Retired">Retired</option>
          <option value="Student">Student</option>
          <option value="Self-Employed">Self-Employed</option>
        </FormSelect>

        {errors.employment && (
          <div className="error-banner">
            <AlertCircle size={18} /> {errors.employment}
          </div>
        )}

        {employment_status === "Employed" && (
          <>
            {employment_records.map((record, index) => (
              <div key={index} className="info-card employment-card">
                <div className="card-header">
                  <div className="card-title-wrapper">
                    <span className="card-title">Job {index + 1}</span>
                    <div className="employment-icon">
                      <Briefcase size={20} />
                    </div>
                  </div>
                  {employment_records.length > 1 && (
                    <button
                      className="remove-btn"
                      onClick={() => {
                        const updated = employment_records.filter((_, i) => i !== index);
                        setEmploymentRecords(updated);
                        setData({ ...data, employment_records: updated });
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <div className="form-grid">
                  <FormInput
                    label="Employer Details"
                    placeholder="Company Name and Address"
                    value={record.employer_details}
                    onChange={(e) => updateRecord(index, "employer_details", e.target.value)}
                    icon={Building}
                  />
                  <FormInput
                    label="Job Title"
                    placeholder="Your Position/Role"
                    value={record.job_title}
                    onChange={(e) => updateRecord(index, "job_title", e.target.value)}
                  />
                  <FormInput
                    label="Salary"
                    placeholder="e.g., $50,000/year"
                    value={record.salary}
                    onChange={(e) => updateRecord(index, "salary", e.target.value)}
                    icon={DollarSign}
                  />
                  <FormInput
                    label="Start Date"
                    type="date"
                    value={record.job_start_date}
                    onChange={(e) => updateRecord(index, "job_start_date", e.target.value)}
                    icon={Calendar}
                  />
                  {!record.is_current_job && (
                    <FormInput
                      label="End Date"
                      type="date"
                      value={record.job_end_date}
                      onChange={(e) => updateRecord(index, "job_end_date", e.target.value)}
                      icon={Calendar}
                    />
                  )}
                  <FormSelect
                    label="Country"
                    value={record.country}
                    onChange={(e) => updateRecord(index, "country", e.target.value)}
                    icon={Globe}
                  >
                    <option value="" disabled>Select Country</option>
                    {COUNTRY_LIST.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </FormSelect>
                  <div className="checkbox-field full-width">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={record.is_current_job}
                        onChange={(e) => updateRecord(index, "is_current_job", e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      This is my current job
                    </label>
                  </div>
                </div>
              </div>
            ))}

            <button type="button" onClick={addRecord} className="add-button">
              <Plus size={16} /> Add Another Job
            </button>
          </>
        )}

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
      </div>
    </FormSection>
  );
}

function Finance({ data, setData, onComplete, onBack, onSaveProgress }) {
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    // No validation required - all fields are optional
    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(data)));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 6); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <FormSection
      title="Financial Information"
      icon={DollarSign}
      description="Please provide details of your financial situation to support your application."
      isComplete={isComplete}
    >
      <div className="finance-container">
        <div className="form-grid">
          <FormInput
            label="Available Current Balance"
            name="bank_balance"
            placeholder="Amount in your bank account"
            value={data.bank_balance}
            onChange={handleInputChange}
            icon={CreditCard}
            error={errors.bank_balance}
          />
          <FormInput
            label="Other Liquid Assets"
            name="financial_status"
            placeholder="Cash, investments, or other liquid assets"
            value={data.financial_status}
            onChange={handleInputChange}
            icon={DollarSign}
          />
          <FormInput
            label="Annual Income"
            name="annual_income"
            placeholder="Your yearly income"
            value={data.annual_income || ""}
            onChange={handleInputChange}
            icon={DollarSign}
            error={errors.annual_income}
          />
          <FormInput
            label="Source of Funds"
            name="fund_source"
            placeholder="Employment, business, investments, etc."
            value={data.fund_source || ""}
            onChange={handleInputChange}
            error={errors.fund_source}
          />
        </div>

        <div className="info-box">
          <div className="info-icon">
            <Info size={20} />
          </div>
          <div className="info-content">
            <p>Sufficient financial resources demonstrate your ability to support yourself during your stay without resorting to unauthorized employment.</p>
          </div>
        </div>

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
      </div>
    </FormSection>
  );
}

function TiesToHomeCountry({ data, setData, onComplete, onBack, onSaveProgress }) {
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    // No validation required - all fields are optional
    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(data)));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 7); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  return (
    <FormSection
      title="Ties to Home Country"
      icon={Home}
      description="Information about your connections to your home country helps establish your intent to return after your visit."
      isComplete={isComplete}
    >
      <div className="ties-container">
        <div className="form-grid">
          <FormTextarea
            label="Property Ownership or Significant Assets"
            placeholder="Describe any properties, investments, or valuable assets you own in your home country"
            value={data.property_ownership}
            onChange={(e) => setData({ ...data, property_ownership: e.target.value })}
            icon={Home}
            error={errors.property_ownership}
          />
          <FormTextarea
            label="Long-term Commitments or Responsibilities"
            placeholder="Describe any family responsibilities, job commitments, or community involvements"
            value={data.long_term_commitments}
            onChange={(e) => setData({ ...data, long_term_commitments: e.target.value })}
            icon={Users}
            error={errors.long_term_commitments}
          />
        </div>

        <div className="info-box">
          <div className="info-icon">
            <Star size={20} />
          </div>
          <div className="info-content">
            <p>Strong ties to your home country demonstrate your intention to return after your visit, which is an important factor in visa approval.</p>
          </div>
        </div>

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
      </div>
    </FormSection>
  );
}

function HealthSecurity({ data, setData, onComplete, onBack, onSaveProgress }) {
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    // No validation required - all fields are optional
    return true;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Save progress before continuing
      saveProgress(true);
    }
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(data)));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 8); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          setIsComplete(true);
          setTimeout(() => onComplete(), 300);
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleHealthIssueChange = (value) => {
    setData({ ...data, has_health_issues: value });
  };

  const handleDiseaseChange = (e) => {
    setData({ ...data, disease_name: e.target.value, custom_disease: "" });
  };

  const handleCustomDiseaseChange = (e) => {
    setData({ ...data, custom_disease: e.target.value });
  };

  const handleCriminalRecordChange = (value) => {
    setData({ ...data, has_criminal_record: value });
  };

  const handleCaseNameChange = (e) => {
    setData({ ...data, case_name: e.target.value, custom_case: "" });
  };

  const handleCustomCaseChange = (e) => {
    setData({ ...data, custom_case: e.target.value });
  };

  return (
    <FormSection
      title="Health and Security"
      icon={Shield}
      description="This information is required for immigration purposes and is treated with strict confidentiality."
      isComplete={isComplete}
    >
      <div className="health-container">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Do you have any significant health issues?</label>
            <YesNoButtons
              value={data.has_health_issues}
              onChange={handleHealthIssueChange}
              error={errors.has_health_issues}
            />
          </div>

          {data.has_health_issues === "yes" && (
            <>
              <FormSelect
                label="Health Condition"
                value={data.disease_name}
                onChange={handleDiseaseChange}
                icon={HeartPulse}
                error={errors.disease_name}
              >
                <option value="">Select Condition</option>
                {DISEASE_OPTIONS.map((disease, index) => (
                  <option key={index} value={disease}>{disease}</option>
                ))}
              </FormSelect>

              {data.disease_name === "Other" && (
                <FormInput
                  label="Specify Health Condition"
                  placeholder="Please describe your health condition"
                  value={data.custom_disease}
                  onChange={handleCustomDiseaseChange}
                />
              )}
            </>
          )}

          <div className="form-group">
            <label className="form-label">Do you have a criminal record?</label>
            <YesNoButtons
              value={data.has_criminal_record}
              onChange={handleCriminalRecordChange}
              error={errors.has_criminal_record}
            />
          </div>

          {data.has_criminal_record === "yes" && (
            <>
              <FormSelect
                label="Type of Offense"
                value={data.case_name}
                onChange={handleCaseNameChange}
                icon={Scale}
                error={errors.case_name}
              >
                <option value="">Select Offense</option>
                {CASE_OPTIONS.map((caseName, index) => (
                  <option key={index} value={caseName}>{caseName}</option>
                ))}
              </FormSelect>

              {data.case_name === "Other" && (
                <FormInput
                  label="Specify Offense"
                  placeholder="Please describe the offense"
                  value={data.custom_case}
                  onChange={handleCustomCaseChange}
                />
              )}
            </>
          )}
        </div>

        <div className="info-box security-info">
          <div className="info-icon">
            <Shield size={20} />
          </div>
          <div className="info-content">
            <p>All health and security information is kept strictly confidential and is used solely for immigration assessment purposes.</p>
          </div>
        </div>

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
      </div>
    </FormSection>
  );
}

function AdditionalInformation({ data, setData, onComplete, onBack, setShowSuccessMessage, onSaveProgress }) {
  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(data.attachments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update parent data when files change
  useEffect(() => {
    setData(prev => ({ ...prev, attachments: uploadedFiles }));
  }, [uploadedFiles, setData]);

  const validateForm = () => {
    // No validation required - all fields are optional
    return true;
  };

  const saveProgress = (continueAfterSave = false) => {
    setIsSaving(true);
    
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(data)));
    formDataToSubmit.append('email', data.email);
    formDataToSubmit.append('current_step', 9); // Current step

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        setIsSaving(false);
        
        if (continueAfterSave) {
          handleSubmit();
        }
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        setIsSaving(false);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleSubmit = () => {
    if (validateForm()) {
      setIsSubmitting(true);

      // Prepare form data for submission
      const formDataToSubmit = new FormData();

      // Add all form data as JSON (excluding files)
      const formDataJson = { ...processFormData(data), attachments: [] }; // Don't include files in JSON
      formDataToSubmit.append('application', JSON.stringify(formDataJson));
      formDataToSubmit.append('email', data.email); // Add email for backend lookup

      // Add files
      uploadedFiles.forEach((file, index) => {
        formDataToSubmit.append(`attachments[${index}]`, file);
      });

      // Submit to backend - this will update the existing record with the email
      fetch(`${API_BASE_URL}/submit-by-email`, {
        method: 'POST',
        body: formDataToSubmit,
      })
        .then(response => response.json())
        .then(result => {
          console.log('Application submitted successfully:', result);
          setIsComplete(true);

          // Show success message
          setShowSuccessMessage(true);

          // Reset form after 4-5 seconds
          setTimeout(() => {
            setShowSuccessMessage(false);
            // Reset form data
            setData({
              full_name: "", date_of_birth: "", nationality: "", email: "",
              contact_number: "", address: "", marital_status: "", spouse_name: "",
              spouse_dob: "", spouse_nationality: "", has_children: "",
              number_of_children: 0, children_details: [],
              user_education_forms: [], spouse_education_forms: [],
              travel_info: { countries_traveled: [] }, country_details: {},
              living_history: [],
              employment_status: "", employment_records: [],
              financial_status: "", bank_balance: "", annual_income: "", fund_source: "",
              property_ownership: "", long_term_commitments: "",
              has_health_issues: "", disease_name: "", custom_disease: "",
              has_criminal_record: "", case_name: "", custom_case: "",
              mothers_name: "", mothers_phone: "", fathers_name: "", fathers_phone: "", parents_address: "",
              has_canadian_contacts: "", number_of_contacts: "", contacts: [],
              has_accommodation: "", accommodation_address: "", comments: "",
              attachments: [],
            });

            // Reset to first step
            window.location.reload(); // Simple way to reset the form
          }, 4500); // 4.5 seconds

          // Call onComplete without parameters
          onComplete();
        })
        .catch(error => {
          console.error('Error submitting application:', error);
          alert('There was an error submitting your application. Please try again.');
          setIsSubmitting(false);
        });
    }
  };

  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...data.contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    setData({ ...data, contacts: updatedContacts });
  };

  return (
    <FormSection
      title="Additional Information"
      icon={User}
      description="Please provide additional details that might be relevant to your application."
      isComplete={isComplete}
    >
      <div className="additional-container">
        <div className="form-grid">
          <FormInput
            label="Mother's Name"
            placeholder="Mother's Full Name"
            value={data.mothers_name || ""}
            onChange={(e) => setData({ ...data, mothers_name: e.target.value })}
            icon={User}
            error={errors.mothers_name}
          />
          <FormInput
            label="Mother's Phone Number"
            type="tel"
            placeholder="Mother's Phone Number"
            value={data.mothers_phone || ""}
            onChange={(e) => setData({ ...data, mothers_phone: e.target.value })}
            error={errors.mothers_phone}
          />
          <FormInput
            label="Father's Name"
            placeholder="Father's Full Name"
            value={data.fathers_name || ""}
            onChange={(e) => setData({ ...data, fathers_name: e.target.value })}
            icon={User}
            error={errors.fathers_name}
          />
          <FormInput
            label="Father's Phone Number"
            type="tel"
            placeholder="Father's Phone Number"
            value={data.fathers_phone || ""}
            onChange={(e) => setData({ ...data, fathers_phone: e.target.value })}
            error={errors.fathers_phone}
          />
          <FormTextarea
            label="Where Do Your Parents Live?"
            placeholder="Parents' Address"
            value={data.parents_address || ""}
            onChange={(e) => setData({ ...data, parents_address: e.target.value })}
            rows={3}
            icon={Home}
            error={errors.parents_address}
          />
        </div>

        <div className="section-divider">
          <div className="divider-line"></div>
          <div className="divider-text">Canadian Contacts</div>
          <div className="divider-line"></div>
        </div>

        <div className="form-group">
          <label className="form-label">Do you have contacts or references in Canada?</label>
          <YesNoButtons
            value={data.has_canadian_contacts}
            onChange={(value) => {
              const has_contacts = value === "yes";
              setData({
                ...data,
                has_canadian_contacts: value,
                contacts: has_contacts ? (data.contacts || []) : [],
                number_of_contacts: has_contacts ? (data.number_of_contacts || 1) : 0
              });
            }}
          />
        </div>

        {data.has_canadian_contacts === "yes" && (
          <>
            <FormSelect
              label="Number of Contacts"
              value={data.number_of_contacts || ""}
              onChange={(e) => {
                const num = parseInt(e.target.value, 10);
                setData({
                  ...data,
                  number_of_contacts: num,
                  contacts: Array(num).fill({ name: "", number: "" }),
                });
              }}
              error={errors.number_of_contacts}
            >
              <option value="">Select Number of Contacts</option>
              {[...Array(5)].map((_, index) => (
                <option key={index} value={index + 1}>{index + 1}</option>
              ))}
            </FormSelect>

            <div className="contacts-container">
              {data.contacts.map((contact, index) => (
                <div key={index} className="info-card contact-card">
                  <div className="card-header">
                    <div className="card-title-wrapper">
                      <span className="card-title">Contact {index + 1}</span>
                      <div className="contact-icon">
                        <User size={20} />
                      </div>
                    </div>
                  </div>
                  <div className="form-grid">
                    <FormInput
                      label="Contact Name"
                      placeholder={`Contact ${index + 1} Name`}
                      value={contact.name || ""}
                      onChange={(e) => handleContactChange(index, "name", e.target.value)}
                      icon={User}
                    />
                    <FormInput
                      label="Contact Number"
                      placeholder={`Contact ${index + 1} Number`}
                      value={contact.number || ""}
                      onChange={(e) => handleContactChange(index, "number", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="section-divider">
          <div className="divider-line"></div>
          <div className="divider-text">Accommodation in Canada</div>
          <div className="divider-line"></div>
        </div>

        <div className="form-group">
          <label className="form-label">Do you have accommodation arranged in Canada?</label>
          <YesNoButtons
            value={data.has_accommodation}
            onChange={(value) => {
              const has_accommodation = value === "yes";
              setData({
                ...data,
                has_accommodation: value,
                accommodation_address: has_accommodation ? (data.accommodation_address || "") : ""
              });
            }}
          />
        </div>

        {data.has_accommodation === "yes" && (
          <FormInput
            label="Accommodation Address"
            placeholder="Full address of your accommodation in Canada"
            value={data.accommodation_address || ""}
            onChange={(e) => setData({ ...data, accommodation_address: e.target.value })}
            icon={MapPin}
            error={errors.accommodation_address}
          />
        )}

        {/* File Upload Section */}
        <div className="section-divider">
          <div className="divider-line"></div>
          <div className="divider-text">Supporting Documents</div>
          <div className="divider-line"></div>
        </div>

        <FileUpload
          files={uploadedFiles}
          setFiles={setUploadedFiles}
          maxFiles={10}
          maxSizeMB={5}
        />

        <FormTextarea
          label="Additional Comments"
          placeholder="Any other information you would like to include with your application"
          value={data.comments || ""}
          onChange={(e) => setData({ ...data, comments: e.target.value })}
          rows={5}
        />

        <NavigationButtons
          onBack={onBack}
          onSave={() => saveProgress(false)}
          onContinue={() => saveProgress(true)}
          isSaving={isSaving || isSubmitting}
          continueLabel={isSubmitting ? "Submitting..." : "Submit Application"}
        />
      </div>
    </FormSection>
  );
}
function UnderReviewMessage({ email, onStartNewApplication }) {
  return (
    <div className="status-message-container">
      <div className="status-card under-review-card">
        <div className="status-icon">
          <Clock size={48} />
        </div>
        
        <h2>Application Under Review</h2>
        
        <div className="status-message">
          <p>Your visa application associated with <strong>{email}</strong> is currently under review.</p>
          <p>Our team is carefully evaluating your submission. You will be notified once a decision has been made.</p>
        </div>
        
        <div className="status-actions">
          <div className="info-box">
            <Info size={18} />
            <span>Please do not submit another application for the same purpose</span>
          </div>
          
          <button 
            onClick={onStartNewApplication}
            className="btn btn-danger secondary-button"
          >
            Start a New Application for Different Purpose
          </button>
        </div>
        
        <div className="contact-support">
          <p>If you have questions about your application status, please contact support:</p>
          <a href="mailto:support@visaapplication.com" className="support-link">
            support@visaapplication.com
          </a>
        </div>
      </div>
    </div>
  );
}
// === MAIN COMPONENT ===
function Travel_Reg_Form1() {
  const [step, setStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isExistingApplication, setIsExistingApplication] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const totalSteps = 9;

  const stepLabels = [
    "Personal Info",
    "Education",
    "Travel History",
    "Living History",
    "Employment",
    "Finance",
    "Home Ties",
    "Health & Security",
    "Additional Info",
  ];

  const [formData, setFormData] = useState({
    // Personal
    full_name: "", date_of_birth: "", nationality: "", email: "",
    contact_number: "", address: "", marital_status: "", spouse_name: "",
    spouse_dob: "", spouse_nationality: "", has_children: "",
    number_of_children: 0, children_details: [],
    // Education
    user_education_forms: [], spouse_education_forms: [],
    // Travel
    travel_info: { countries_traveled: [] }, country_details: {},
    // Living
    living_history: [],
    // Employment
    employment_status: "", employment_records: [],
    // Finance
    financial_status: "", bank_balance: "", annual_income: "", fund_source: "",
    // Ties
    property_ownership: "", long_term_commitments: "",
    // Health & Security
    has_health_issues: "", disease_name: "", custom_disease: "",
    has_criminal_record: "", case_name: "", custom_case: "",
    // Additional
    has_canadian_contacts: "", number_of_contacts: "", contacts: [],
    has_accommodation: "", accommodation_address: "", comments: "",
    mothers_name: "", mothers_phone: "", fathers_name: "", fathers_phone: "", parents_address: "",
    attachments: [],
  });

  // Check for existing application on component mount
  // useEffect(() => {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   // const email = urlParams.get('email');
  //   const email = formData.email;
    
  //   if (email) {
  //     // Load existing application
  //     fetch(`${API_BASE_URL}/email/${email}`)
  //       .then(response => response.json())
  //       .then(result => {
  //         if (result.data && result.data.length > 0) {
  //           const application = result.data[0];
  //           setFormData(application);
  //           setStep(application.current_step || 1);
  //           setIsExistingApplication(true);
  //         }
  //         setIsLoading(false);
  //       })
  //       .catch(error => {
  //         console.error('Error loading application:', error);
  //         setIsLoading(false);
  //       });
  //   } else {
  //     setIsLoading(false);
  //   }
  // }, [formData.email]);


  useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const emailFromUrl = urlParams.get('email');
  const emailToUse = emailFromUrl || formData.email;
  
  if (emailToUse) {
    setIsCheckingEmail(true);
    // Load existing application
    fetch(`${API_BASE_URL}/email/${emailToUse}`)
      .then(response => response.json())
      .then(result => {
        if (result.data && result.data.length > 0) {
          const application = result.data[0];
          // Only update if we're not already showing this data
          if (application.email !== formData.email) {
            setFormData(application);
            setStep(application.current_step || 1);
            setIsExistingApplication(true);
            setApplicationStatus(application.status || null);
            setIsCheckingEmail(false);
            // If application is under review, show message
            // if (application.status === 'under_review') {
            //   // Don't proceed to form
            //   setEmailLoaded(false);
            // }
          }
        }
        setIsLoading(false);
        setIsCheckingEmail(false);
      })
      .catch(error => {
        console.error('Error loading application:', error);
        setIsLoading(false);
        setIsCheckingEmail(false);
      });
  } else {
    setIsLoading(false);
    setIsCheckingEmail(false);
  }
}, []); // Empty dependency array - only run on mount

// Add a separate effect to handle email changes after initial load
useEffect(() => {
  if (formData.email && !isLoading) {
    // Debounce the API call to avoid excessive requests
    const handler = setTimeout(() => {
      setIsCheckingEmail(true);
      fetch(`${API_BASE_URL}/email/${formData.email}`)
        .then(response => response.json())
        .then(result => {
          if (result.data && result.data.length > 0) {
            const application = result.data[0];
            // Only update if we're not already showing this data
            if (application.email === formData.email) {
              setFormData(prev => ({ ...prev, ...application }));
              setStep(application.current_step || 1);
              setIsExistingApplication(true);
              setApplicationStatus(application.status || null);
              setIsCheckingEmail(false);
            }
          }else{
            setIsCheckingEmail(false);
          }
        })
        .catch(error => {
          setIsCheckingEmail(false);
          console.error('Error loading application by email:', error);
        });
    }, 1000); // 1 second debounce

    return () => clearTimeout(handler);
  }
}, [formData.email, isLoading]);

  const handleStepComplete = (stepNumber) => {
    setCompletedSteps(prev => new Set([...prev, stepNumber]));
  };

  const nextStep = () => {
    if (step < totalSteps) {
      setStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSaveProgress = () => {
    // Prepare form data for saving
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('application', JSON.stringify(processFormData(formData)));
    formDataToSubmit.append('email', formData.email);
    formDataToSubmit.append('current_step', step);

    // Save to API
    fetch(`${API_BASE_URL}/update-by-email`, {
      method: 'POST',
      body: formDataToSubmit,
    })
      .then(response => response.json())
      .then(result => {
        console.log('Progress saved successfully:', result);
        alert('Your progress has been saved successfully!');
      })
      .catch(error => {
        console.error('Error saving progress:', error);
        alert('There was an error saving your progress. Please try again.');
      });
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    console.log("Final Form Data:", formData);
    // The success message will now be handled by AdditionalInformation
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <PersonalInformation
          data={formData}
          setData={setFormData}
          onComplete={nextStep}
          onBack={step > 1 ? prevStep : null}
          onSaveProgress={handleSaveProgress}
          isExistingApplication={isExistingApplication}
          isCheckingEmail={isCheckingEmail} // Pass isLoading prop
        />;
      case 2:
        return <Education
          data={formData}
          setData={setFormData}
          globalMaritalStatus={formData.marital_status}
          onComplete={nextStep}
          onBack={prevStep}
          onSaveProgress={handleSaveProgress}
        />;
      case 3:
        return <TravelInformation
          data={formData}
          setData={setFormData}
          onComplete={nextStep}
          onBack={prevStep}
          onSaveProgress={handleSaveProgress}
        />;
      case 4:
        return <LivingHistory
          data={formData}
          setData={setFormData}
          onComplete={nextStep}
          onBack={prevStep}
          onSaveProgress={handleSaveProgress}
        />;
      case 5:
        return <Employment
          data={formData}
          setData={setFormData}
          onComplete={nextStep}
          onBack={prevStep}
          onSaveProgress={handleSaveProgress}
        />;
      case 6:
        return <Finance
          data={formData}
          setData={setFormData}
          onComplete={nextStep}
          onBack={prevStep}
          onSaveProgress={handleSaveProgress}
        />;
      case 7:
        return <TiesToHomeCountry
          data={formData}
          setData={setFormData}
          onComplete={nextStep}
          onBack={prevStep}
          onSaveProgress={handleSaveProgress}
        />;
      case 8:
        return <HealthSecurity
          data={formData}
          setData={setFormData}
          onComplete={nextStep}
          onBack={prevStep}
          onSaveProgress={handleSaveProgress}
        />;
      case 9:
        return <AdditionalInformation
          data={formData}
          setData={setFormData}
          onComplete={handleSubmit}
          onBack={prevStep}
          setShowSuccessMessage={setShowSuccessMessage}
          onSaveProgress={handleSaveProgress}
        />;
      default:
        return <div>Invalid step</div>;
    }
  };

 // Show under review message if application status is under_review
  if (applicationStatus === 'under_review') {
    return (
      <div className="application-container">
        <UnderReviewMessage 
          email={formData.email}
          onStartNewApplication={() => {
            window.location.reload();
            // setApplicationStatus(null);
            // setFormData(initialFormData);
            // setEmailLoaded(true);
          }}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="application-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="application-container">
      <Query />
      <SuccessMessage show={showSuccessMessage} />
      {!showSuccessMessage && (
        <>
          <div className="header">
            <div className="header-content">
              <h1>Visa Application Form</h1>
              <p>Complete all sections to submit your application. Your progress is automatically saved.</p>
              {isExistingApplication && (
                <div className="existing-application-notice">
                  <Info size={16} />
                  <span>Existing application found. Continuing from where you left off.</span>
                </div>
              )}
            </div>
            <div className="header-decoration">
              <Sparkles size={48} />
            </div>
          </div>

          <ProgressBar currentStep={step} totalSteps={totalSteps} />

          <Stepper
            currentStep={step}
            steps={stepLabels}
          />

          <div className="form-container">
            <div className="form-content">
              {renderStep()}
            </div>
          </div>

          <div className="form-footer">
            <div className="footer-info">
              <Circle size={12} /> <span>Progress saved automatically</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Travel_Reg_Form1;